import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
    Box, Typography, Paper, Grid, Chip, Skeleton, IconButton, Tooltip,
} from '@mui/material';
import { PlayArrow, Sensors, History } from '@mui/icons-material';
import { API_ENDPOINTS } from '../../../utils/config';
import { THEME } from '../../../theme';
import { gamesData } from '../../../Components/Arcade/gamesConfig';
import {
    buildGameNameMap, gameDisplayName, formatAgentLabel, resolveUserLabel, toEpochMs,
} from './displayHelpers';
import { EmptyState, ErrorState } from '../../../Components/Arcade/ui/StateViews';

const POLL_MS = 12000;
const RECENT_LIMIT = 5;
// Ephemeral SDK sessions finish quickly; training mega-sessions can run much
// longer. Age-out only applies to the DB fallback path when the orchestrator
// has no live discovery — allow a longer window for training-tagged rows.
const MAX_LIVE_SESSION_AGE_MS = 10 * 60 * 1000;
const MAX_TRAINING_LIVE_SESSION_AGE_MS = 6 * 60 * 60 * 1000;

function SessionCard({ item, mode, gameNameMap }) {
    const gameId = item.game_id || item.game_type || item.game;
    const runId = item.run_id || item._id || item.id;
    const sessionId = item.session_id || item.db_session_id || item._id || item.id;
    const isLive = mode === 'live';
    const playPath = isLive
        ? (sessionId ? `/arcade/spectate/${sessionId}?game=${encodeURIComponent(gameId || '')}` : null)
        : (runId ? `/arcade/replay/${runId}` : null);
    const startedAt = item.created_at || item.timestamp || item.updated_at;
    const trainingStats = item.training_stats || null;
    const isTraining = item.mode === 'training'
        || item.source === 'self_play_train'
        || Boolean(trainingStats);
    const roundIndex = trainingStats?.round_index ?? item.round_index;
    const numEpisodes = trainingStats?.num_episodes ?? item.num_episodes;
    const wins = trainingStats?.wins;
    const avgScores = trainingStats?.avg_scores;
    const avgScoreHint = avgScores
        ? Object.values(avgScores).reduce((a, b) => a + Number(b || 0), 0)
            / Math.max(1, Object.keys(avgScores).length)
        : null;

    return (
        <Paper sx={{
            p: 2.5,
            bgcolor: 'rgba(255,255,255,0.02)',
            border: `1px solid ${THEME.border}`,
            borderRadius: 3,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {isLive ? (
                    <Chip
                        size="small"
                        icon={<Sensors sx={{ fontSize: '14px !important' }} />}
                        label="Live"
                        sx={{
                            bgcolor: 'rgba(44, 187, 93, 0.15)',
                            color: THEME.primary,
                            fontWeight: 700,
                            '& .MuiChip-icon': { color: THEME.primary },
                        }}
                    />
                ) : (
                    <Chip
                        size="small"
                        icon={<History sx={{ fontSize: '14px !important' }} />}
                        label="Recent"
                        sx={{
                            bgcolor: 'rgba(255,255,255,0.04)',
                            color: THEME.secondary,
                            fontWeight: 600,
                            '& .MuiChip-icon': { color: THEME.secondary },
                        }}
                    />
                )}
                {isTraining && (
                    <Chip
                        size="small"
                        label="Training"
                        sx={{
                            bgcolor: 'rgba(255, 193, 7, 0.12)',
                            color: '#ffc107',
                            fontWeight: 700,
                        }}
                    />
                )}
                <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 700 }}>
                    {gameDisplayName(gameId, gameNameMap)}
                </Typography>
            </Box>

            <Typography variant="body2" sx={{ color: THEME.secondary }}>
                {isLive
                    ? (resolveUserLabel(item) !== '—' ? resolveUserLabel(item) : (isTraining ? 'Self-play training' : 'Active session'))
                    : formatAgentLabel(item)}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {!isLive && (
                    <Chip
                        size="small"
                        label={`Score: ${item.score ?? 0}`}
                        sx={{ bgcolor: THEME.primary, color: 'black', fontWeight: 'bold' }}
                    />
                )}
                {isLive && isTraining && roundIndex != null && numEpisodes != null && (
                    <Chip
                        size="small"
                        label={`Round ${roundIndex}/${numEpisodes}`}
                        sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: 'white', fontWeight: 600 }}
                    />
                )}
                {isLive && isTraining && avgScoreHint != null && (
                    <Chip
                        size="small"
                        label={`Avg ${avgScoreHint.toFixed(0)}`}
                        sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: THEME.secondary, fontWeight: 600 }}
                    />
                )}
                {isLive && isTraining && wins && (
                    <Typography variant="caption" sx={{ color: '#888' }}>
                        Wins {Object.entries(wins).map(([k, v]) => `${k.replace('player_', 'P')}:${v}`).join(' · ')}
                    </Typography>
                )}
                {startedAt && (
                    <Typography variant="caption" sx={{ color: '#666' }}>
                        {new Date(toEpochMs(startedAt)).toLocaleString()}
                    </Typography>
                )}
            </Box>

            {playPath && (
                <Tooltip title={isLive ? 'Watch live session' : 'Play replay'}>
                    <IconButton
                        component={Link}
                        to={playPath}
                        size="small"
                        aria-label={isLive ? 'Watch live session' : 'Play replay'}
                        sx={{ mt: 'auto', color: THEME.primary, alignSelf: 'flex-start' }}
                    >
                        <PlayArrow fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}
        </Paper>
    );
}

async function fetchLiveSessions() {
    // Prefer orchestrator in-memory sessions (truly in progress).
    try {
        const liveUrl = `${API_ENDPOINTS.SDK_SESSIONS}?mine=0`;
        const res = await fetch(liveUrl);
        if (res.ok) {
            const data = await res.json();
            const list = (data.sessions || []).filter(
                (s) => !s.status || s.status === 'active'
            );
            if (list.length) return list;
        }
    } catch (e) {
        // fall through to persisted sessions
    }

    // Fallback: DB-recorded active sessions (may include stale rows whose
    // completion update never landed — drop anything implausibly old).
    try {
        const res = await fetch(API_ENDPOINTS.SESSIONS({ status: 'active', limit: 20 }));
        if (!res.ok) return [];
        const data = await res.json();
        const now = Date.now();
        return (data.sessions || []).filter((s) => {
            const startedAt = toEpochMs(s.created_at || s.timestamp || s.updated_at);
            const ageMs = startedAt ? now - new Date(startedAt).getTime() : 0;
            const isTraining = s.mode === 'training'
                || s.source === 'self_play_train'
                || Boolean(s.training_stats);
            const maxAge = isTraining
                ? MAX_TRAINING_LIVE_SESSION_AGE_MS
                : MAX_LIVE_SESSION_AGE_MS;
            return !(ageMs > maxAge);
        });
    } catch (e) {
        return [];
    }
}

async function fetchRecentRuns() {
    const res = await fetch(API_ENDPOINTS.RECENT_RUNS({ limit: RECENT_LIMIT }));
    if (!res.ok) throw new Error(`status ${res.status}`);
    const data = await res.json();
    return data.runs || data.submissions || (Array.isArray(data) ? data : []);
}

/**
 * Arcade hub panel: live/in-progress sessions when any exist; otherwise the
 * last 5 recent completed runs with replay links.
 */
const LiveSessionsPanel = () => {
    const [mode, setMode] = useState('live');
    const [items, setItems] = useState([]);
    const [games, setGames] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const catalog = useMemo(() => [...gamesData, ...games], [games]);
    const gameNameMap = useMemo(() => buildGameNameMap(catalog), [catalog]);

    const load = useCallback(async () => {
        setError(null);
        try {
            const [live, gamesRes] = await Promise.all([
                fetchLiveSessions(),
                fetch(API_ENDPOINTS.GAMES_LIST).catch(() => null),
            ]);
            if (gamesRes && gamesRes.ok) {
                const g = await gamesRes.json();
                setGames(Array.isArray(g) ? g : (g.games || []));
            }

            if (live.length > 0) {
                setMode('live');
                setItems(live);
                return;
            }

            const recent = await fetchRecentRuns();
            setMode('recent');
            setItems((recent || []).slice(0, RECENT_LIMIT));
        } catch (e) {
            setError('Failed to load sessions');
            setItems([]);
            setMode('recent');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        let mounted = true;
        let timer;

        async function tick() {
            if (!mounted) return;
            await load();
            if (mounted) timer = setTimeout(tick, POLL_MS);
        }

        setLoading(true);
        tick();
        return () => {
            mounted = false;
            if (timer) clearTimeout(timer);
        };
    }, [load]);

    const title = mode === 'live' ? 'Live Now' : 'Recent Games';
    const subtitle = mode === 'live'
        ? 'Sessions currently in progress'
        : 'No live games — showing the last 5 completed runs';

    return (
        <Paper sx={{
            mb: 5,
            bgcolor: THEME.cardBg,
            border: `1px solid ${THEME.border}`,
            borderRadius: 3,
            overflow: 'hidden',
        }}>
            <Box sx={{
                px: 3,
                pt: 2.5,
                pb: 1.5,
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: 2,
                flexWrap: 'wrap',
                borderBottom: `1px solid ${THEME.border}`,
            }}>
                <Box>
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 800 }}>
                        {title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: THEME.secondary }}>
                        {subtitle}
                    </Typography>
                </Box>
                {mode === 'live' && !loading && items.length > 0 && (
                    <Chip
                        size="small"
                        label={`${items.length} live`}
                        sx={{ bgcolor: 'rgba(44, 187, 93, 0.15)', color: THEME.primary, fontWeight: 700 }}
                    />
                )}
            </Box>

            <Box sx={{ p: 3 }}>
                {loading && (
                    <Grid container spacing={2}>
                        {[...Array(mode === 'live' ? 3 : RECENT_LIMIT)].map((_, i) => (
                            <Grid item xs={12} sm={6} md={4} key={i}>
                                <Skeleton
                                    variant="rectangular"
                                    height={140}
                                    sx={{ bgcolor: 'rgba(255,255,255,0.08)', borderRadius: 2 }}
                                />
                            </Grid>
                        ))}
                    </Grid>
                )}

                {!loading && error && (
                    <ErrorState message="Live games can't be shown right now. Please try again in a moment." />
                )}

                {!loading && !error && !items.length && (
                    <EmptyState message="No live sessions or recent games yet — play a game to get started." />
                )}

                {!loading && !error && items.length > 0 && (
                    <Grid container spacing={2}>
                        {items.map((item, idx) => (
                            <Grid
                                item
                                xs={12}
                                sm={6}
                                md={4}
                                key={item.session_id || item.run_id || item._id || item.id || idx}
                            >
                                <SessionCard
                                    item={item}
                                    mode={mode}
                                    gameNameMap={gameNameMap}
                                />
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Box>
        </Paper>
    );
};

export default LiveSessionsPanel;
